import { getApp, getApps, initializeApp } from "firebase/app";
import {
  Analytics,
  isSupported,
  getAnalytics,
  logEvent,
} from "firebase/analytics";
import { getAuth, RecaptchaVerifier } from "firebase/auth";
import type { Metric } from "web-vitals";

type AnalyticsParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    recaptchaVerifier: RecaptchaVerifier;
  }
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const auth = getAuth(app);
let analyticsPromise: Promise<Analytics | null> | null = null;

const isAnalyticsConfigured = () =>
  Boolean(firebaseConfig.appId && firebaseConfig.measurementId);

const sanitizeAnalyticsParams = (params?: AnalyticsParams) => {
  if (!params) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  );
};

const getClientAnalytics = async (): Promise<Analytics | null> => {
  if (
    analyticsPromise ||
    typeof window === "undefined" ||
    !isAnalyticsConfigured()
  ) {
    return analyticsPromise ?? null;
  }

  analyticsPromise = isSupported()
    .then((supported) => (supported ? getAnalytics(app) : null))
    .catch((error) => {
      console.warn("Firebase analytics unavailable:", error);
      return null;
    });

  return analyticsPromise;
};

const trackAnalyticsEvent = async (
  eventName: string,
  params?: AnalyticsParams,
) => {
  const analytics = await getClientAnalytics();

  if (!analytics) {
    return;
  }

  logEvent(analytics, eventName, sanitizeAnalyticsParams(params));
};

const trackPageView = async (pagePath: string) => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }

  await trackAnalyticsEvent("page_view", {
    page_path: pagePath,
    page_title: document.title,
    page_location: window.location.href,
  });
};

const trackWebVitalMetric = (metric: Metric) => {
  if (window.location.pathname === "/buy" || window.location.pathname === "/buy/") {
    return;
  }
  void trackAnalyticsEvent("web_vital", {
    metric_name: metric.name,
    metric_value: Math.round(metric.value),
    metric_delta: Math.round(metric.delta),
    metric_id: metric.id,
  });
};

const getCaptchaVerifier = (
  captchaContainerId: string,
  onSuccess?: () => {},
  onExpired?: () => {},
) => {
  window.recaptchaVerifier = new RecaptchaVerifier(auth, captchaContainerId, {
    size: "invisible",
    callback: (response: any) => {
      if (onSuccess) {
        onSuccess();
      }
    },
    "expired-callback": () => {
      if (onExpired) {
        onExpired();
      }
    },
  });

  return window.recaptchaVerifier;
};

const resetCaptcha = async () => {
  if (!window.recaptchaVerifier) {
    return;
  }

  const widgetId = await window.recaptchaVerifier.render();
  (window as any).grecaptcha.reset(widgetId);
};

export {
  app,
  auth,
  resetCaptcha,
  getCaptchaVerifier,
  getClientAnalytics,
  trackAnalyticsEvent,
  trackPageView,
  trackWebVitalMetric,
  isAnalyticsConfigured,
};
